import { useCallback } from "react";
import type { ScopedThreadRef } from "@amoreg/contracts";
import {
  isAtomCommandInterrupted,
  squashAtomCommandFailure,
} from "@amoreg/client-runtime/state/runtime";
import { useEnvironmentHttpBaseUrl } from "~/state/environments";
import { assetEnvironment } from "~/state/assets";
import { useAtomQueryRunner } from "~/state/use-atom-query-runner";
import { stackedThreadToast, toastManager } from "~/components/ui/toast";
import { downloadWorkspaceFile } from "./downloadWorkspaceFile";

/** Cards and the file panel mint the same scoped original-file download. */
export function useWorkspaceFileDownload(threadRef: ScopedThreadRef) {
  const httpBaseUrl = useEnvironmentHttpBaseUrl(threadRef.environmentId);
  const createAssetUrl = useAtomQueryRunner(assetEnvironment.createUrl, {
    reportFailure: false,
    refresh: true,
  });
  return useCallback(
    (filePath: string) => {
      void (async () => {
        try {
          if (!httpBaseUrl)
            throw new Error("The file server is disconnected. Reconnect and try again.");
          const result = await downloadWorkspaceFile({
            threadRef,
            filePath,
            httpBaseUrl,
            createAssetUrl,
          });
          if (result._tag === "Success" || isAtomCommandInterrupted(result)) return;
          throw squashAtomCommandFailure(result);
        } catch (error) {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: "Unable to download file",
              description: error instanceof Error ? error.message : "An error occurred.",
            }),
          );
        }
      })();
    },
    [createAssetUrl, httpBaseUrl, threadRef],
  );
}
